import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch, RootState } from "../store";
import { searchInternalOrder, SearchOrderRequest, getOrder } from "src/network/graphql/paymentService";
import { Order, OrderDetails } from "../stripeTransactionHistory/stripeTransactionHistorySlice";

const initialOrder: Order[] = [];

interface InternalOrderState {
  loading: boolean;
  error: unknown;
  orders: {
    list: Order[];
    total: number;
  };
  orderDetails: Record<string, OrderDetails>;
  loadingOrderDetails: Record<string, boolean>;
}

const internalOrderSlice = createSlice({
  name: "internalOrder",
  initialState: {
    loading: false,
    error: null,
    orders: {
      list: initialOrder,
      total: 0
    },
    orderDetails: {},
    loadingOrderDetails: {}
  } as InternalOrderState,
  reducers: {
    fetchOrderListStart: (state) => {
      state.loading = true;
    },
    fetchOrderListSuccess: (state, action) => {
      state.loading = false;
      state.orders.list = action.payload?.list ?? [];
      state.orders.total = action.payload?.total ?? 0;
    },
    fetchOrderListFail: (state, action) => {
      state.loading = false;
      state.orders.list = [];
      state.orders.total = 0;
      state.error = action.payload;
    },
    fetchOrderDetailsStart: (state, action) => {
      state.loadingOrderDetails[action.payload] = true;
    },
    fetchOrderDetailsSuccess: (state, action) => {
      const { orderId, items } = action.payload;

      state.orderDetails[orderId] = { items };
      delete state.loadingOrderDetails[orderId];
    },
    fetchOrderDetailsFail: (state, action) => {
      const orderId = action.payload.orderId;

      delete state.loadingOrderDetails[orderId];
      state.error = action.payload.error;
    }
  }
});

const {
  fetchOrderListStart,
  fetchOrderListSuccess,
  fetchOrderListFail,
  fetchOrderDetailsStart,
  fetchOrderDetailsSuccess,
  fetchOrderDetailsFail
} = internalOrderSlice.actions;

export const fetchInternalOrderListAction = (data: SearchOrderRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchOrderListStart());
    try {
      const response = await searchInternalOrder(data);

      const structuredResponse = response.data.internal_search_order?.items ?? [];

      dispatch(
        fetchOrderListSuccess({
          list: structuredResponse,
          total: response.data.internal_search_order?.total_size ?? 0
        })
      );
    } catch (error) {
      dispatch(fetchOrderListFail(error));
    }
  };
};

export const fetchInternalOrderDetailsAction = (orderId: string) => {
  return async (dispatch: StoreDispatch, getState: () => RootState) => {
    const state = getState();
    const existingDetails = state.internalOrder.orderDetails[orderId];
    const isLoading = state.internalOrder.loadingOrderDetails[orderId];

    if (existingDetails || isLoading) {
      return;
    }

    dispatch(fetchOrderDetailsStart(orderId));

    try {
      const response = await getOrder(orderId);
      const orderData = response.data.get_order;

      dispatch(
        fetchOrderDetailsSuccess({
          orderId,
          items: orderData?.items || []
        })
      );
    } catch (error) {
      dispatch(
        fetchOrderDetailsFail({
          orderId,
          error
        })
      );
    }
  };
};

export default internalOrderSlice.reducer;
